import React, { useCallback } from 'react'
import cx from 'clsx'

import { MenuDivider, MenuItem } from '@szhsin/react-menu'
import { NavLink, useNavigate } from 'react-router-dom'

import { useGetMyAccount, useLogOut } from '@src/api/queries/Account'
import { useIsAdmin } from '@src/helpers'
import { useGetConnectedChannel } from '@src/helpers/hooks/useGetConnectedChannel'
import { useHandleClickVendors } from '@src/helpers/hooks/useHandleClickVendors'
import { useTranslation } from 'react-i18next'

import routes from '@src/Routes/routes'
import { SLACK_BOT } from '@src/constants'

import styles from '../index.module.css'
import { IconSettings, IconRocket, IconLogout, IconUser } from '@tabler/icons-react'

interface AccountMenuProps {
  className?: string
}

const AccountMenu: React.FC<AccountMenuProps> = ({ className }) => {
  const { t } = useTranslation('common', { keyPrefix: 'navigation' })
  const { data: user } = useGetMyAccount()
  const { mutateAsync: logOut } = useLogOut()
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const connectedChannel = useGetConnectedChannel()
  const hasAnyAppConnected = !!connectedChannel
  const { handleClickVendor } = useHandleClickVendors()

  const signOut = useCallback(async () => {
    await logOut({})
    navigate(routes.public.login)
  }, [logOut, navigate])

  return (
    <div className={className}>
      <div className="px-4 pt-0.5 flex flex-col">
        <p className="leading-5 pb-0.5 text-sm font-bold text-grey_1">{t('account')}</p>
        <MenuItem className={styles.menuItem}>
          <NavLink className="w-full leading-6 text-base font-medium text-black truncate" to={routes.private.profile}>
            {user?.team?.name}
          </NavLink>
        </MenuItem>
        <MenuItem className={styles.menuItem}>
          <NavLink className="w-full leading-6 text-base text-grey_1 truncate" to={routes.private.profile}>
            {user?.email}
          </NavLink>
        </MenuItem>
      </div>
      {isAdmin && !hasAnyAppConnected && (
        <>
          <MenuDivider className="!border-gray-100" />
          <div className="flex flex-col">
            <MenuItem className={styles.menuItem}>
              <NavLink
                className={cx(styles.navLinkButton, 'group text-jungle_green text-base w-full')}
                to={SLACK_BOT.link as string}
                onClick={handleClickVendor}
                data-vendor="Slack"
                data-qa="slack-connect-link"
              >
                <img src={SLACK_BOT.img} className={cx(styles.navIcon, 'group-hover:scale-105')} />
                {t('addToSlack')}
              </NavLink>
            </MenuItem>
            <MenuItem className={styles.menuItem}>
              <NavLink
                className={cx(styles.navLinkButton, 'group text-jungle_green text-base w-full')}
                to={routes.private.installTeams}
                data-qa="teams-connect-link"
                onClick={handleClickVendor}
                data-vendor="Teams"
              >
                <img src="/teams.png" className={cx(styles.navIcon, 'group-hover:scale-105')} />
                {t('addToTeams')}
              </NavLink>
            </MenuItem>
          </div>
        </>
      )}
      <MenuDivider className="!border-gray-100" />
      <div className="flex flex-col">
        <MenuItem className={cx(styles.menuItem, 'group')}>
          <NavLink
            className={cx(styles.navLinkButton, '!py-1 !px-2  !mx-2 text-base w-full')}
            to={routes.private.profile}
            onContextMenu={(e) => e.stopPropagation()}
          >
            <IconUser className={cx(styles.navIcon, 'group-hover:scale-105')} />
            {t('profile')}
          </NavLink>
        </MenuItem>
        {isAdmin && (
          <MenuItem className={cx(styles.menuItem, 'group')}>
            <NavLink
              className={cx(styles.navLinkButton, '!py-1 !px-2  !mx-2 text-base w-full')}
              to={routes.private.teamSettings}
              onContextMenu={(e) => e.stopPropagation()}
            >
              <IconSettings className={cx(styles.navIcon, 'group-hover:scale-105')} />
              {t('teamSettings')}
            </NavLink>
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem className={cx(styles.menuItem, 'group')}>
            <NavLink
              className={cx(styles.navLinkButton, '!py-1 !px-2  !mx-2 text-base w-full')}
              to={routes.private.billing}
              onContextMenu={(e) => e.stopPropagation()}
            >
              <IconRocket className={cx(styles.navIcon, 'group-hover:scale-105')} />
              {t('subscription')}
            </NavLink>
          </MenuItem>
        )}
      </div>
      {isAdmin && <MenuDivider className="!border-gray-100" />}

      <div className="flex flex-col">
        <MenuItem className={cx(styles.menuItem, 'group')}>
          <button
            className={cx(styles.navLinkButton, '!py-1 !px-2 !my-0 !mx-2 text-base flex w-full leading-5')}
            onClick={signOut}
          >
            <IconLogout className={cx(styles.navIcon, 'ml-[1px] group-hover:scale-105')} />
            {t('logout')}
          </button>
        </MenuItem>
      </div>
    </div>
  )
}

export default AccountMenu
